// Oda fiyatlandırma yardımcıları — saf fonksiyonlar (React/ağ bağımlılığı yok).
//
// Fiyat kuralı (2026-27 kış sezonu, ODA FİYATLARI.xlsx):
//   gece fiyatı = takvimdeki oda fiyatı (oda + tarih + YETİŞKİN sayısı)
//               + 7-12 yaş çocuk sayısı × oda tipinin çocuk ücreti (room_types.childFee, gecelik)
//   0-6 yaş ücretsiz; 13 yaş ve üstü yetişkin sayılır. Çocuklar kişi kademesine dahil edilmez.
//
// Veri modeli (offers.rooms[] içindeki her oda):
//   price         → ortalama gecelik fiyat (eski kodla uyumluluk için hep yazılır)
//   total         → oda toplamı (eski tekliflerde yok)
//   nightlyRates  → [{ date: 'YYYY-MM-DD', price, source }] (eski tekliflerde yok)
//                   source: 'calendar' = fiyat takviminden (çocuk ücreti dahil)
//                           'default'  = o gece takvimde bu oda için hiç fiyat yok (tanımlı dönem dışı) → 0
//                           'manual'   = elle girildi / eski tekliften geldi
//                   (Proje sahibi kararı 2026-09-16: takvim dışındaki gecelerin fiyatı 0'dır;
//                    room_types.price artık fiyatlamada KULLANILMAZ.)
//                   Yalnızca formda (kaydedilmez): 'missing' = takvimde o gece var ama bu yetişkin
//                   sayısı için fiyat yok · 'error' = takvim okunamadı · 'pending' = bekleniyor
//   adults        → yetişkin (13+)
//   children      → toplam çocuk (eski kodla uyumluluk: childrenFree + childrenPaid)
//   childrenFree  → 0-6 yaş çocuk (yeni tekliflerde)
//   childrenPaid  → 7-12 yaş çocuk (yeni tekliflerde)
//   childFee      → fiyatlama anındaki çocuk başı gecelik ücret (yeni tekliflerde)
//
// Teklif geneli indirim (offers.discount*) → aşağıda "Teklif indirimi" bölümü.
//
// Tarihler her yerde 'YYYY-MM-DD' string; hesaplar UTC ile yapılır ki yaz saati
// geçişinde ya da tarayıcı saat diliminde gün kaymasın.

const DAY_MS = 24 * 60 * 60 * 1000;
const WEEKDAY_SHORT = ['Paz', 'Pzt', 'Sal', 'Çar', 'Per', 'Cum', 'Cmt'];

export const CHILD_FREE_MAX_AGE = 6;   // 0-6 yaş ücretsiz
export const CHILD_PAID_MAX_AGE = 12;  // 7-12 yaş çocuk ücreti; 13+ yetişkin

// Tek seferde fiyatlanabilecek en uzun konaklama (sunucudaki RATE_MAX_RANGE_DAYS ile aynı)
export const MAX_NIGHTS = 366;
// Geçerli yıl aralığı. Tarih alanına yıl klavyeyle yazılırken tarayıcı '0002-10-12' gibi ara değerler
// üretir; Date.UTC 0–99 yıllarını 1900+y saydığı için bunlar on binlerce gecelik aralık olurdu.
const MIN_YEAR = 2000;
const MAX_YEAR = 2100;

export const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

const parseUTC = (dateStr) => {
  if (typeof dateStr !== 'string' || !/^\d{4}-\d{2}-\d{2}/.test(dateStr)) return null;
  const [y, m, d] = dateStr.slice(0, 10).split('-').map(Number);
  if (y < MIN_YEAR || y > MAX_YEAR) return null;
  return Date.UTC(y, m - 1, d);
};

const formatUTC = (ms) => new Date(ms).toISOString().slice(0, 10);

// Gece sayısı — dizi üretmeden (büyük aralıkta arayüzü dondurmasın diye). Geçersizse 0.
export const nightCount = (checkIn, checkOut) => {
  const start = parseUTC(checkIn);
  const end = parseUTC(checkOut);
  if (start === null || end === null || end <= start) return 0;
  return Math.round((end - start) / DAY_MS);
};

// Konaklama geceleri: giriş günü DAHİL, çıkış günü HARİÇ.
// enumerateNights('2026-10-12', '2026-10-15') → ['2026-10-12', '2026-10-13', '2026-10-14']
// MAX_NIGHTS'ı aşan aralıkta boş dizi döner (çağıran nightCount ile ayrıca uyarı gösterir).
export const enumerateNights = (checkIn, checkOut) => {
  const count = nightCount(checkIn, checkOut);
  if (count === 0 || count > MAX_NIGHTS) return [];
  const start = parseUTC(checkIn);
  const nights = [];
  for (let i = 0; i < count; i++) nights.push(formatUTC(start + i * DAY_MS));
  return nights;
};

// Aynı aralıktaki ay-sonu gibi uçlar için: bir tarihe gün ekle.
export const addDays = (dateStr, days) => {
  const ms = parseUTC(dateStr);
  return ms === null ? '' : formatUTC(ms + days * DAY_MS);
};

// 0=Pazar … 6=Cumartesi (JS getDay ile aynı)
export const weekdayOf = (dateStr) => {
  const ms = parseUTC(dateStr);
  return ms === null ? null : new Date(ms).getUTCDay();
};

// '2026-10-12' → '12.10 Pzt'
export const formatNightLabel = (dateStr) => {
  const ms = parseUTC(dateStr);
  if (ms === null) return dateStr;
  const d = new Date(ms);
  const dd = String(d.getUTCDate()).padStart(2, '0');
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  return `${dd}.${mm} ${WEEKDAY_SHORT[d.getUTCDay()]}`;
};

// Fiyat alanındaki değeri tutara çevirir: sayı aynen; metin Türkçe biçimle ("29.500" → 29500).
// Formda gece fiyatı yazılırken ham metin tutulur; hesaplarda HER ZAMAN bu kullanılmalı
// (Number('29.500') = 29,5 olur). Geçersizse NaN.
export const toAmount = (value) => {
  if (value === '' || value === null || value === undefined) return NaN;
  return typeof value === 'number' ? value : parsePriceInput(String(value));
};

// Her gece önce kuruşa yuvarlanır — ekrandaki toplam, kaydedilen rooms[].total ve totalPrice
// böylece hep aynı çıkar (ör. 3 × 33,335 girilse bile). Geçersiz/boş gece 0 sayılır.
export const sumNightly = (nightlyRates) =>
  round2((nightlyRates || []).reduce((acc, n) => acc + (round2(toAmount(n.price)) || 0), 0));

// Türkçe biçimli fiyat girişini sayıya çevirir. Geçersizse NaN.
//   "4.500" → 4500 (nokta = binlik)   "4500,50" → 4500.5   "4.500,50" → 4500.5
//   "4.5" → 4.5 (binlik kalıbına uymayan tek nokta = ondalık)   "₺4.500" / "4.500 TL" → 4500
// Dikkat: Number('4.500') = 4.5 olduğu için kullanıcı girişinde Number() doğrudan KULLANILMAMALI.
export const parsePriceInput = (raw) => {
  if (typeof raw === 'number') return Number.isFinite(raw) ? raw : NaN;
  if (typeof raw !== 'string') return NaN;
  let s = raw.replace(/\s+/g, '').replace(/₺|TL|tl/g, '');
  if (s === '' || !/^[0-9.,]+$/.test(s)) return NaN;
  const hasDot = s.includes('.');
  const hasComma = s.includes(',');
  if (hasDot && hasComma) {
    // Son görülen ayraç ondalıktır: "4.500,50" (TR) ya da "4,500.50" (EN)
    if (s.lastIndexOf(',') > s.lastIndexOf('.')) s = s.replace(/\./g, '').replace(',', '.');
    else s = s.replace(/,/g, '');
  } else if (hasComma) {
    const parts = s.split(',');
    s = parts.length > 2 ? parts.join('') : s.replace(',', '.');
  } else if (hasDot) {
    if (/^\d{1,3}(\.\d{3})+$/.test(s)) s = s.replace(/\./g, '');
    else if ((s.match(/\./g) || []).length > 1) return NaN;
  }
  const n = Number(s);
  return Number.isFinite(n) ? n : NaN;
};

// Bir odanın toplamı. Yeni tekliflerde `total`, eskilerde price × nights.
// PDF / Dashboard / Raporlar / teklif detayı hep bunu kullanmalı.
export const roomLineTotal = (room) => {
  if (!room) return 0;
  if (room.total !== undefined && room.total !== null && room.total !== '') return Number(room.total) || 0;
  return (Number(room.price) || 0) * (Number(room.nights) || 0);
};

// ─── Teklif indirimi ───────────────────────────────────────────────────────────
// Teklif başına EN FAZLA BİR indirim (proje sahibi kararı 2026-09-19), tüm odaların toplamına uygulanır.
// offers tablosu: discountName ("Erken Rezervasyon"), discountType ('percentage' | 'amount'),
//                 discountValue (yüzde ya da tutar), discountAmount (düşülen tutar).
// offers.totalPrice = Σ roomLineTotal − discountAmount (müşterinin ödeyeceği tutar).
// Odaların kendi toplamları (rooms[].total) indirimsiz kalır.
export const DISCOUNT_TYPES = ['percentage', 'amount'];
export const DEFAULT_DISCOUNT_NAME = 'Erken Rezervasyon';

// Girişte seçili türle çelişen birim var mı: Tutar seçiliyken "%10", Yüzde seçiliyken "20 TL" / "₺20".
// (Sessizce silinse "%10" 10 TL indirim olurdu.)
export const discountUnitMismatch = (raw, type) => {
  const s = String(raw ?? '');
  if (type === 'amount') return s.includes('%');
  if (type === 'percentage') return /₺|tl/i.test(s);
  return false;
};

// Formdaki indirim değerini sayıya çevirir: "10", "%10", "12,5" → yüzde; "5.000", "5000 TL" → tutar.
// Geçersizse ya da birim seçili türle çelişiyorsa (type verildiğinde) NaN.
export const parseDiscountInput = (raw, type) => {
  if (typeof raw === 'number') return parsePriceInput(raw);
  if (discountUnitMismatch(raw, type)) return NaN;
  return parsePriceInput(String(raw ?? '').replace(/%/g, ''));
};

// subTotal (indirimsiz toplam) üzerinden indirim. Değer yok / 0 / geçersiz → indirim yok.
// Yüzde indiriminde düşülen tutar TAM para birimine yuvarlanır (%5 × 89.250 = 4.462,50 → 4.463):
// liste, WhatsApp ve e-posta tutarı tam lira gösterdiği için PDF'teki toplamla kuruş farkı olmasın.
// Tutar indirimi kullanıcının yazdığı gibi (kuruşa yuvarlı). Yüzde 100'e, tutar ara toplama kırpılır.
export const computeDiscount = ({ subTotal, type, value }) => {
  const base = round2(subTotal);
  const v = Number(value);
  if (!DISCOUNT_TYPES.includes(type) || !Number.isFinite(v) || v <= 0 || !(base > 0)) {
    return { amount: 0, total: base };
  }
  const amount = type === 'percentage'
    ? Math.min(Math.round((base * Math.min(v, 100)) / 100), base)
    : round2(Math.min(v, base));
  return { amount, total: round2(base - amount) };
};

// Kayıtlı teklifin indirimi (PDF, teklif detayı, raporlar). Eski tekliflerde alanlar yok → amount 0.
// DECIMAL kolonlar API'den string gelir ("10.00") → Number ile okunur (makine biçimi, Türkçe değil).
export const offerDiscount = (offer) => {
  const rooms = Array.isArray(offer?.rooms) ? offer.rooms : [];
  const subTotal = rooms.length > 0
    ? round2(rooms.reduce((acc, r) => acc + roomLineTotal(r), 0))
    : round2(Number(offer?.totalPrice) || 0);
  const type = offer?.discountType;
  const value = Number(offer?.discountValue);
  const { amount, total } = computeDiscount({ subTotal, type, value });
  return {
    name: (offer?.discountName || '').trim() || DEFAULT_DISCOUNT_NAME,
    type: amount > 0 ? type : null,
    value: amount > 0 ? value : 0,
    amount, subTotal, total,
  };
};

// Para gösterimi: tam sayıysa kuruşsuz ("12.000"), değilse iki hane ("39.062,50")
export const formatMoneyTR = (n) => {
  const v = Number(n) || 0;
  return v.toLocaleString('tr-TR', { minimumFractionDigits: Number.isInteger(v) ? 0 : 2, maximumFractionDigits: 2 });
};

// "Erken Rezervasyon (%10)" — tutar indiriminde yalnızca ad
export const discountLabel = ({ name, type, value }) =>
  type === 'percentage'
    ? `${name} (%${Number(value).toLocaleString('tr-TR', { maximumFractionDigits: 2 })})`
    : name;

// Oda bazlı ciro (Dashboard/Raporlar) için: teklif indirimini odalara oranla dağıtır.
// Oda cirosu = roomLineTotal(oda) × offerNetFactor(teklif). İndirim yoksa 1.
export const offerNetFactor = (offer) => {
  const { amount, subTotal, total } = offerDiscount(offer);
  return amount > 0 && subTotal > 0 ? total / subTotal : 1;
};

// Gecelik fiyat gösterimi: tüm geceler aynıysa o fiyat, değilse ortalama (isAverage=true).
export const nightlyDisplay = (room) => {
  const rates = room?.nightlyRates;
  if (Array.isArray(rates) && rates.length > 0) {
    const prices = rates.map(n => Number(n.price) || 0);
    const allSame = prices.every(p => p === prices[0]);
    return { value: allSame ? prices[0] : round2(sumNightly(rates) / rates.length), isAverage: !allSame };
  }
  return { value: Number(room?.price) || 0, isAverage: false };
};

// GET /api/roomRates satırlarını (tek oda tipi için) takvim önbelleğine çevirir.
//   rows  → [{ date, persons, price }]
//   dates → sorgulanan geceler; satırı olmayan gece için {} yazılır ("sorgulandı, takvimde fiyat yok")
// Sonuç: { 'YYYY-MM-DD': { [persons]: price } }
export const ratesCacheFromRows = (rows, dates = []) => {
  const cache = {};
  dates.forEach(d => { cache[d] = {}; });
  (rows || []).forEach(r => {
    if (!cache[r.date]) cache[r.date] = {};
    cache[r.date][Number(r.persons)] = Number(r.price);
  });
  return cache;
};

// Gecelik fiyat listesini kurar (saf; aynı girdiye hep aynı çıktı).
//   nights       → enumerateNights çıktısı
//   rateCache    → ratesCacheFromRows çıktısı. Anahtarı OLMAYAN gece henüz sorgulanmamıştır.
//   persons      → yetişkin sayısı (fiyat kademesi)
//   childrenPaid → 7-12 yaş çocuk sayısı
//   childFee     → çocuk başı gecelik ücret (room_types.childFee) — yalnızca takvim fiyatı olan gecelere eklenir
//   pinned       → { date: { price, source } } sabitlenmiş geceler: elle girilen ya da kayıtlı
//                  tekliften yüklenen fiyatlar. Bunlar takvimden yeniden hesaplanmaz.
//   autoPricing  → false ise (TL dışı para birimi) sabitlenmemiş gecelere fiyat YAZILMAZ (0, elle)
//   rateError    → takvim okunamadı: sorgulanamayan geceler 0 ile 'error' (kırmızı) işaretlenir
// Takvim gecelerinde ayrıca { base, childTotal } döner (ekranda döküm göstermek için).
export const buildNightlyRates = ({
  nights, rateCache = {}, persons = 1, childrenPaid = 0, childFee,
  pinned = {}, autoPricing = true, rateError = false,
}) => {
  const childTotal = round2(Math.max(0, Number(childrenPaid) || 0) * (Number(childFee) || 0));
  const p = Number(persons) || 1;
  return nights.map(date => {
    const pin = pinned[date];
    if (pin) return { date, price: pin.price, source: pin.source };
    if (!autoPricing) return { date, price: 0, source: 'manual' };

    if (Object.prototype.hasOwnProperty.call(rateCache, date)) {
      const rates = rateCache[date] || {};
      // Tanımlı fiyat dönemi dışı: oda da çocuk da 0
      if (Object.keys(rates).length === 0) return { date, price: 0, source: 'default' };
      if (rates[p] !== undefined) {
        const base = Number(rates[p]);
        return { date, price: round2(base + childTotal), source: 'calendar', base, childTotal };
      }
      return { date, price: '', source: 'missing' };
    }

    if (rateError) return { date, price: 0, source: 'error' };
    return { date, price: '', source: 'pending' };
  });
};

// Takvimde bu oda için tanımlı en yüksek yetişkin sayısı (önbellekteki gecelere göre). Yoksa 0.
export const maxPricedPersons = (rateCache) =>
  Object.values(rateCache || {}).reduce((m, rates) => Math.max(m, ...Object.keys(rates || {}).map(Number), 0), 0);

// "2 Yetişkin, 1 Çocuk (7-12), 1 Çocuk (0-6)" — eski tekliflerde "2 Yetişkin, 1 Çocuk"
export const occupancyLabel = (room) => {
  const adults = Number(room?.adults) || 1;
  const hasSplit = room && (room.childrenFree !== undefined || room.childrenPaid !== undefined);
  if (!hasSplit) return `${adults} Yetişkin, ${Number(room?.children) || 0} Çocuk`;
  const parts = [`${adults} Yetişkin`];
  const paid = Number(room.childrenPaid) || 0;
  const free = Number(room.childrenFree) || 0;
  if (paid > 0) parts.push(`${paid} Çocuk (7-12)`);
  if (free > 0) parts.push(`${free} Çocuk (0-6)`);
  return parts.join(', ');
};

// Oda toplamı elle değiştirildiğinde yeni toplamı gecelere dağıtır.
// Gecelerin birbirine oranı korunur (yüzde indirim uygulamak gibi); fiyatlar tam TL'ye yuvarlanır.
// Yuvarlama farkı EN PAHALI geceye yüklenir — böylece eşit fiyatlı geceler eşit kalır
// (4.500 + 4.500 + 5.200 → 14.000 = 4.437 + 4.437 + 5.126). Tüm geceler 0 ise eşit dağıtır.
export const distributeTotal = (nightlyRates, targetTotal) => {
  const n = (nightlyRates || []).length;
  if (n === 0) return [];
  const target = round2(Math.max(0, Number(targetTotal) || 0));
  const whole = Math.floor(target);
  const cents = round2(target - whole);

  const current = nightlyRates.map(r => Math.max(0, toAmount(r.price) || 0));
  const weightSum = current.reduce((a, b) => a + b, 0);
  const weights = weightSum > 0 ? current : current.map(() => 1);
  const wSum = weightSum > 0 ? weightSum : n;
  const exact = weights.map(w => (whole * w) / wSum);

  // Farkı emecek gece: payı en büyük olan (eşitlikte sonuncusu)
  let absorber = 0;
  exact.forEach((x, i) => { if (x >= exact[absorber]) absorber = i; });

  let shares = exact.map(x => Math.round(x));
  shares[absorber] += whole - shares.reduce((a, b) => a + b, 0);

  // Çok küçük hedeflerde (ör. 4 geceye 2 TL) emici gece eksiye düşebilir → en büyük kalan yöntemi
  if (shares[absorber] < 0) {
    shares = exact.map(Math.floor);
    let remaining = whole - shares.reduce((a, b) => a + b, 0);
    exact
      .map((x, i) => ({ i, frac: x - Math.floor(x) }))
      .sort((a, b) => b.frac - a.frac || a.i - b.i)
      .forEach(({ i }) => { if (remaining > 0) { shares[i] += 1; remaining -= 1; } });
  }
  shares[absorber] = round2(shares[absorber] + cents);

  return nightlyRates.map((r, i) => ({ ...r, price: shares[i], source: 'manual' }));
};

// Kayıtlı bir teklif odasının gecelik dökümünü düzenleme ekranı için kurar.
// - Geçerli bir nightlyRates varsa (tarihler geceyle birebir) aynen kullanılır.
// - Yoksa (eski teklif ya da bozuk kayıt) her gece eşit fiyatla kurulur ve kayıtlı toplam
//   (total ya da price × nights) kuruşu kuruşuna korunur. Geceler 'manual' sayılır ki
//   takvim sonradan değişse bile düzenlenen teklifin fiyatı kendiliğinden oynamasın.
export const legacyNightlyRates = (room) => {
  const nights = enumerateNights(room?.checkIn, room?.checkOut);
  if (nights.length === 0) return [];

  const saved = room?.nightlyRates;
  if (Array.isArray(saved) && saved.length === nights.length && saved.every((n, i) => n?.date === nights[i])) {
    return saved.map(n => ({
      date: n.date, price: Number(n.price) || 0, source: n.source || 'manual',
      ...(n.childTotal > 0 ? { base: Number(n.base) || 0, childTotal: Number(n.childTotal) } : {}),
    }));
  }

  const flat = nights.map(date => ({ date, price: Number(room.price) || 0, source: 'manual' }));
  return distributeTotal(flat, roomLineTotal({ ...room, nights: nights.length }));
};
